import type { FeatureEvents, ScreenEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject } from '../new-analytics-types';

type ScreenKeys =
  | 'initiativesTableScreen'
  | 'createInitiativeScreen'
  | 'plansScreen'
  | 'planDetailsScreen'
  | 'createMigrationNameScreen'
  | 'createMigrationMethodScreen'
  | 'createMigrationPreChecksScreen'
  | 'createMigrationReviewScreen'
  | 'createMigrationUsersGroupsScreen'
  | 'createMigrationProjectsScreen'
  | 'createMigrationSelectProjectsScreen'
  | 'createMigrationRemediationsScreen'
  | 'createMigrationSpacesScreen'
  | 'createMigrationSelectSpacesScreen'
  | 'planChecksScreen'
  | 'planChecksRemediationsScreen';
type UIEventKeys =
  | 'createNewInitiativeButtonClick'
  | 'startMaterializationButtonClick'
  | 'startReplicationButtonClick'
  | 'initiativeCreateMigrationButtonClick'
  | 'createConnectionButtonClick'
  | 'updateDeltaButtonClick'
  | 'plansCreateMigrationButtonClick'
  | 'migrateNowButtonClick'
  | 'migrateLaterButtonClick'
  | 'migrateDeltaButtonClick'
  | 'migrateLastDeltaButtonClick'
  | 'migrateFullButtonClick'
  | 'selectProjectsWithCSVButtonClick'
  | 'selectProjectsWithCSVDownloadButtonClick'
  | 'selectProjectsWithCSVUploadButtonClick';
type TrackEventKeys = never;
type OperationalEventKeys = never;
export enum LibertyScreens {
  initiativesTableScreen = 'initiativesTableScreen',
  createInitiativeScreen = 'createInitiativeScreen',
  plansScreen = 'plansScreen',
  planDetailsScreen = 'planDetailsScreen',
  createPlanScreen = 'createPlanScreen',
  createMigrationNameScreen = 'createMigrationNameScreen',
  createMigrationMethodScreen = 'createMigrationMethodScreen',
  createMigrationPreChecksScreen = 'createMigrationPreChecksScreen',
  createMigrationReviewScreen = 'createMigrationReviewScreen',
  createMigrationUsersGroupsScreen = 'createMigrationUsersGroupsScreen',
  createMigrationProjectsScreen = 'createMigrationProjectsScreen',
  createMigrationSelectProjectsScreen = 'createMigrationSelectProjectsScreen',
  createMigrationRemediationsScreen = 'createMigrationRemediationsScreen',
  createMigrationSpacesScreen = 'createMigrationSpacesScreen',
  createMigrationSelectSpacesScreen = 'createMigrationSelectSpacesScreen',
  planChecksScreen = 'planChecksScreen',
  planChecksRemediationsScreen = 'planChecksRemediationsScreen',
}

export const libertyEventData: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, OperationalEventKeys> = {
  confluenceSpec: ['https://hello.atlassian.net/wiki/spaces/CT5/pages/6820270023/Liberty+UI+-+Enhanced+Analytics+Central+Event+Registry'],
  screen: {
    initiativesTableScreen: (attributes: { orgId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.initiativesTableScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91331',
    }),
    createInitiativeScreen: (attributes: { orgId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createInitiativeScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91332',
    }),
    plansScreen: (attributes: { orgId: string; connectionId: string; materializationId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.plansScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91333',
    }),
    planDetailsScreen: (attributes: { orgId: string; connectionId: string; materializationId: string; planId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.planDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91334',
    }),
    createMigrationNameScreen: (attributes: { orgId: string; materializationId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationNameScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100430',
    }),
    createMigrationMethodScreen: (attributes: { orgId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationMethodScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100458',
    }),
    createMigrationPreChecksScreen: (attributes: { orgId: string; materializationId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationPreChecksScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100459',
    }),
    createMigrationReviewScreen: (attributes: { orgId: string; connectionId: string; materializationId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationReviewScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100460',
    }),
    createMigrationUsersGroupsScreen: (attributes: { orgId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationUsersGroupsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100461',
    }),
    createMigrationProjectsScreen: (attributes: { orgId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationProjectsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100462',
    }),
    createMigrationSelectProjectsScreen: (attributes: { orgId: string; materializationId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationSelectProjectsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100463',
    }),
    createMigrationRemediationsScreen: (attributes: { orgId: string; materializationId: string; planId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationRemediationsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100470',
    }),
    createMigrationSpacesScreen: (attributes: { orgId: string; materializationId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationSpacesScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100471',
    }),
    createMigrationSelectSpacesScreen: (attributes: { orgId: string; materializationId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.createMigrationSelectSpacesScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100472',
    }),
    planChecksScreen: (attributes: { orgId: string; materializationId: string; planId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.planChecksScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100474',
    }),
    planChecksRemediationsScreen: (attributes: { orgId: string; materializationId: string; planId: string }): ScreenEvent => ({
      data: {
        name: LibertyScreens.planChecksRemediationsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/100475',
    }),
  },
  ui: {
    createNewInitiativeButtonClick: (attributes: { orgId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createNewInitiativeButton',
        attributes,
        source: LibertyScreens.initiativesTableScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91494',
    }),
    createConnectionButtonClick: (attributes: { orgId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createConnectionButton',
        attributes,
        source: LibertyScreens.createInitiativeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91558',
    }),
    startMaterializationButtonClick: (attributes: { orgId: string; connectionId: string; replicationId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'startMaterialisationButton',
        attributes,
        source: LibertyScreens.createInitiativeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91562',
    }),
    startReplicationButtonClick: (attributes: { orgId: string; connectionId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'startReplicationButton',
        attributes,
        source: LibertyScreens.createInitiativeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91563',
    }),
    initiativeCreateMigrationButtonClick: (attributes: { orgId: string; connectionId: string; materializationId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createMigrationButton',
        attributes,
        source: LibertyScreens.createInitiativeScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91565',
    }),
    updateDeltaButtonClick: (attributes: { orgId: string; connectionId: string; materializationId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'updateDeltaButton',
        attributes,
        source: LibertyScreens.plansScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91566',
    }),
    plansCreateMigrationButtonClick: (attributes: { orgId: string; connectionId: string; materializationId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createPlanButton',
        attributes,
        source: LibertyScreens.plansScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91567',
    }),
    migrateNowButtonClick: (attributes: { orgId: string; connectionId: string; materializationId: string; planId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'migrateNowButton',
        attributes,
        source: LibertyScreens.createPlanScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91568',
    }),
    migrateLaterButtonClick: (attributes: { orgId: string; connectionId: string; materializationId: string; planId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'migrateLaterButton',
        attributes,
        source: LibertyScreens.createPlanScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91570',
    }),
    migrateDeltaButtonClick: (attributes: { orgId: string; connectionId: string; materializationId: string; planId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'migrateDeltaButton',
        attributes,
        source: LibertyScreens.planDetailsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91572',
    }),
    migrateLastDeltaButtonClick: (attributes: {
      orgId: string;
      connectionId: string;
      materializationId: string;
      planId: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'migrateLastDeltaButton',
        attributes,
        source: LibertyScreens.planDetailsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91573',
    }),
    migrateFullButtonClick: (attributes: { orgId: string; connectionId: string; materializationId: string; planId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'migrateFullButton',
        attributes,
        source: LibertyScreens.planDetailsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91574',
    }),
    selectProjectsWithCSVButtonClick: (attributes: { orgId: string; connectionId: string; materializationId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'selectProjectsWithCSVButton',
        attributes,
        source: LibertyScreens.createPlanScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91723',
    }),
    selectProjectsWithCSVDownloadButtonClick: (attributes: {
      orgId: string;
      connectionId: string;
      materializationId: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'selectProjectsWithCSVDownloadButton',
        attributes,
        source: LibertyScreens.createPlanScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91724',
    }),
    selectProjectsWithCSVUploadButtonClick: (attributes: { orgId: string; connectionId: string; materializationId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'selectProjectsWithCSVUploadButton',
        attributes,
        source: LibertyScreens.createPlanScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91725',
    }),
  },
  track: {},
  operational: {},
};
